import { supabase } from '@/lib/supabase';
import type { SearchFilters, SemanticScholarPaper } from '@/features/search/types';

// =========================================
// Queries (read operations)
// =========================================

/** Search papers via Supabase edge function */
export async function searchPapers(filters: SearchFilters) {
  return supabase.functions.invoke('search-papers', {
    body: {
      query: filters.query,
      offset: filters.offset ?? 0,
      limit: filters.limit ?? 10,
      year: filters.year,
      fieldsOfStudy: filters.fieldsOfStudy,
      openAccessOnly: filters.openAccessOnly,
    },
  });
}

/** Fetch all paper alerts for a user */
export async function fetchPaperAlerts(userId: string) {
  return supabase
    .from('paper_alerts')
    .select('*')
    .eq('user_id', userId)
    .order('created_at', { ascending: false });
}

/** Check if a source with the given DOI already exists for a user */
export async function fetchSourceByDoi(userId: string, doi: string) {
  return supabase
    .from('sources')
    .select('id')
    .eq('doi', doi)
    .eq('user_id', userId)
    .maybeSingle();
}

// =========================================
// Mutations (write operations)
// =========================================

/** Import a Semantic Scholar paper as a source */
export async function insertSourceFromPaper(
  userId: string,
  paper: SemanticScholarPaper,
) {
  return supabase
    .from('sources')
    .insert({
      user_id: userId,
      file_name: `${paper.title.slice(0, 100)}.pdf`,
      file_type: 'application/pdf',
      title: paper.title,
      authors: paper.authors.map((a) => a.name),
      publication_year: paper.year,
      journal: paper.journal?.name ?? null,
      doi: paper.externalIds?.DOI ?? null,
      abstract: paper.abstract ?? paper.tldr?.text ?? null,
      keywords: paper.fieldsOfStudy ?? [],
      source_url: paper.openAccessPdf?.url ?? paper.url,
      extraction_status: paper.openAccessPdf ? 'pending' : 'failed',
      metadata: {
        semantic_scholar_id: paper.paperId,
        citation_count: paper.citationCount,
        reference_count: paper.referenceCount,
        is_open_access: paper.isOpenAccess,
        publication_types: paper.publicationTypes,
        external_ids: paper.externalIds,
      },
    })
    .select()
    .single();
}

/** Link a source to a folder */
export async function insertFolderSource(folderId: string, sourceId: string) {
  return supabase.from('folder_sources').insert({
    folder_id: folderId,
    source_id: sourceId,
  });
}

/** Create a new paper alert */
export async function insertPaperAlert(params: {
  user_id: string;
  query: string;
  criteria_ids: string[];
  frequency: string;
}) {
  return supabase
    .from('paper_alerts')
    .insert(params)
    .select()
    .single();
}

/** Toggle a paper alert's active status */
export async function updatePaperAlertActive(id: string, active: boolean) {
  return supabase
    .from('paper_alerts')
    .update({ active })
    .eq('id', id);
}

/** Delete a paper alert */
export async function deletePaperAlert(id: string) {
  return supabase
    .from('paper_alerts')
    .delete()
    .eq('id', id);
}
